import { create } from 'zustand';
import { isFallbackTokenImage } from '../lib/tokenFallbacks';

const TOKEN_CACHE_KEY = 'machina:mob-token-images';

export function normalizeTokenImageKey(name: string | undefined | null): string {
  if (!name) return 'creature';
  return name.trim().replace(/\s+\d+$/, '').toLowerCase();
}

interface TokenImageState {
  // Token images by mob name
  tokenImages: Record<string, string>;

  // Actions
  loadFromCache: () => void;
  saveToCache: () => void;
  setTokenImage: (name: string, imageUrl: string) => void;
  deleteTokenImage: (name: string) => void;
  clearAllTokens: () => void;
  getMissingTokens: (names: string[]) => string[];
}

export const useTokenImageStore = create<TokenImageState>((set, get) => ({
  tokenImages: {},

  loadFromCache: () => {
    try {
      const cached = localStorage.getItem(TOKEN_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as Record<string, string>;
        const normalized = Object.entries(parsed).reduce<Record<string, string>>((acc, [name, imageUrl]) => {
          acc[normalizeTokenImageKey(name)] = imageUrl;
          return acc;
        }, {});
        console.log('[TokenImageStore] Loaded cached token images:', Object.keys(normalized).length);
        set({ tokenImages: normalized });
        if (JSON.stringify(parsed) !== JSON.stringify(normalized)) {
          localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(normalized));
        }
      }
    } catch (e) {
      console.warn('[TokenImageStore] Failed to load cached tokens:', e);
    }
  },

  saveToCache: () => {
    const { tokenImages } = get();
    if (Object.keys(tokenImages).length > 0) {
      try {
        localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(tokenImages));
        console.log('[TokenImageStore] Saved token cache:', Object.keys(tokenImages).length, 'tokens');
      } catch (e) {
        console.warn('[TokenImageStore] Failed to save token cache:', e);
      }
    }
  },

  setTokenImage: (name: string, imageUrl: string) => {
    set((state) => {
      const key = normalizeTokenImageKey(name);
      const newTokenImages = { ...state.tokenImages, [key]: imageUrl };
      // Auto-save to cache
      try {
        localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(newTokenImages));
      } catch (e) {
        console.warn('[TokenImageStore] Failed to save token cache:', e);
      }
      return { tokenImages: newTokenImages };
    });
  },

  deleteTokenImage: (name: string) => {
    set((state) => {
      const key = normalizeTokenImageKey(name);
      const { [key]: _, ...rest } = state.tokenImages;
      // Auto-save to cache
      try {
        localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify(rest));
      } catch (e) {
        console.warn('[TokenImageStore] Failed to save token cache:', e);
      }
      return { tokenImages: rest };
    });
  },

  clearAllTokens: () => {
    localStorage.removeItem(TOKEN_CACHE_KEY);
    set({ tokenImages: {} });
  },

  getMissingTokens: (names: string[]) => {
    const { tokenImages } = get();
    const seen = new Set<string>();
    const missing: string[] = [];
    for (const name of names) {
      const key = normalizeTokenImageKey(name);
      // A cached PLACEHOLDER still counts as missing, so generation is retried
      // on a later pass instead of the initials art sticking forever.
      const cached = tokenImages[key];
      if (seen.has(key) || (cached && !isFallbackTokenImage(cached))) continue;
      seen.add(key);
      missing.push(key);
    }
    return missing;
  },
}));
